import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Badge,
  Button,
  Card,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Select,
  SelectAction,
  SelectContent,
  SelectItem,
  SelectValue,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import { CheckCircle, Pencil, Trash, WarningCircle } from 'phosphor-react'

import { productService } from 'api/services/product'
import { Wrapper } from 'components'
import { NewProduct, ProductType } from 'api/types/product'
import { RouteNamesAuth } from 'routes/enum'

export const BackOfficeProducts = () => {
  const navigate = useNavigate()

  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isApiBeingCalled, setIsApiBeingCalled] = useState<boolean>(false)

  // track selected product for deletion
  const [selected, setSelected] = useState<string>('')

  // selected new product to create
  const [newProduct, setNewProduct] = useState<NewProduct | null>(null)

  // products data
  const [data, setData] = useState<ProductType[] | null>(null)
  const [newProducts, setNewProducts] = useState<NewProduct[] | null>(null)

  //modals visibility
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)

  // fetch product list from API
  const getProductList = async () => {
    setIsLoading(true)
    try {
      const response = await productService.getPortalProductList()
      setData(response as ProductType[])
      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar os produtos.')
    }
  }

  // fetch new products from Isaak that are not in the portal yet
  const getNewProducts = async () => {
    setIsLoading(true)
    try {
      const response = await productService.getNewProducts()
      setNewProducts(response as NewProduct[])
      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar os novos produtos.')
    }
  }

  // toggle product active/inactive status
  const changeProductStatus = async (product: string) => {
    if (!product) return
    try {
      const newProduct = data?.find(p => p.product === product)
      if (!newProduct) return
      const newStatus = newProduct.status === 1 ? 0 : 1
      setIsApiBeingCalled(true)
      await productService.savePortalProduct({
        ...newProduct,
        status: newStatus,
      })
      getProductList()
      toast.success('Status do produto alterado com sucesso.')
    } catch (error) {
      toast.error('Não foi possível alterar o status do produto.')
    } finally {
      setIsApiBeingCalled(false)
    }
  }

  // delete selected product
  const deleteProduct = async () => {
    if (!selected) return
    try {
      setIsApiBeingCalled(true)
      await productService.deletePortalProduct({
        product: selected,
      })
      setShowDeleteModal(false)
      getProductList()
      setIsApiBeingCalled(false)
      toast.success('Produto apagado com sucesso.')
    } catch (error) {
      toast.error('Não foi possível apagar o produto.')
    }
  }

  const createProduct = async () => {
    if (!newProduct) return
    setIsApiBeingCalled(true)

    try {
      await productService.savePortalProduct({
        product: newProduct.product,
        categoryId: 1, // default category
        description: newProduct.description,
        content: '',
        simulatorHelperText: '',
        simulatorConditions: '',
        status: 0,
        hasSimulation: false,
        label: newProduct.description,
      })
      setShowCreateModal(false)
      navigate(
        RouteNamesAuth.BACKOFFICE_PRODUCT_DETAILS.replace(
          ':id',
          newProduct.product,
        ),
      )
      setIsApiBeingCalled(false)
      toast.success('Produto criado com sucesso.')
    } catch (error) {
      toast.error('Não foi possível criar o produto.')
    }
  }

  // initial data fetch
  useEffect(() => {
    getProductList()
    getNewProducts()
  }, [])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <h1 className="pb-10 text-xxxl font-medium text-primary-600 flex items-center justify-between">
          <span>Produtos</span>
          <Button
            variant="default"
            color="secondary"
            onClick={() => {
              setShowCreateModal(true)
            }}
          >
            Adicionar Novo Produto
          </Button>
        </h1>

        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Simulador?</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data &&
                  data.map((product: ProductType, index: number) => (
                    <TableRow
                      key={`${product.product}-${index}`}
                      className="even:bg-primary-50/25"
                    >
                      <TableCell className="normal-case">
                        {product.product}
                      </TableCell>
                      <TableCell className="normal-case">
                        {product.description}
                      </TableCell>
                      <TableCell className="normal-case">
                        <span className="flex items-center gap-2">
                          {product.status === 1 ? (
                            <Badge
                              className="pointer-events-none"
                              color="success"
                              variant="base"
                            >
                              Ativo
                            </Badge>
                          ) : (
                            <Badge
                              className="pointer-events-none"
                              color="error"
                              variant="base"
                            >
                              Inativo
                            </Badge>
                          )}
                          <Switch
                            checked={product.status === 1}
                            onCheckedChange={() => {
                              changeProductStatus(product.product)
                            }}
                            className="data-[state=checked]:bg-metal-500 text-metal-500 [&_span]:data-[state=checked]:before:bg-metal-500"
                          />
                        </span>
                      </TableCell>
                      <TableCell className="normal-case">
                        {product.hasSimulation ? (
                          <span className="text-success-500">
                            <CheckCircle weight="fill" size={20} />
                          </span>
                        ) : (
                          <span className="text-error-500">
                            <WarningCircle weight="fill" size={20} />
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="default"
                            color="secondary"
                            onClick={() => {
                              navigate(
                                RouteNamesAuth.BACKOFFICE_PRODUCT_DETAILS.replace(
                                  ':id',
                                  product.product,
                                ),
                              )
                            }}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="default"
                            color="error"
                            disabled={isApiBeingCalled}
                            onClick={() => {
                              setSelected(product.product)
                              setShowDeleteModal(true)
                            }}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </>
        )}
      </Wrapper>

      <Modal open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Produto</ModalTitle>
            <ModalDescription>
              Quer apagar este produto?
              <br />
              <br />
              <strong className="text-error-500">
                Todos os dados associados a este produto serão perdidos.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deleteProduct()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={showCreateModal} onOpenChange={setShowCreateModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Criar Produto</ModalTitle>
            <ModalDescription>
              {newProducts && newProducts.length === 0 && (
                <>
                  <p className="text-error-500 pb-4">
                    Não existem produtos novos para criar.
                  </p>
                </>
              )}

              {newProducts && newProducts.length === 0 && (
                <p className="text-metal-500 pb-4">
                  Selecione o produto que quer criar.
                </p>
              )}

              <Select
                value={newProduct?.product || ''}
                onValueChange={value => {
                  const selected =
                    newProducts?.find(np => np.product === value) || null
                  setNewProduct(selected)
                }}
              >
                <SelectAction>
                  <SelectValue placeholder="Selecione um produto" />
                </SelectAction>
                <SelectContent>
                  {newProducts &&
                    newProducts.map((product: NewProduct) => (
                      <SelectItem key={product.product} value={product.product}>
                        {product.product}- {product.description}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              <br />
              <br />
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="secondary"
              onClick={() => createProduct()}
            >
              Criar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
